import React, { useEffect, useMemo, useState } from "react";
import { createRoot } from "react-dom/client";
import {
  assumptionRegistry,
  bodies,
  claims,
  formulas,
  modelArtifacts,
  processes,
  readinessCategories,
  scenarios,
  sourceRegistryV2,
  sources,
} from "./data/snapshots.js";
import {
  appExportFilenames,
  buildFeasibilityReportExportPayload,
  buildResearchPackagesExportPayload,
  buildScenarioSnapshotExportPayload,
} from "./data/appExportPayloads.js";
import { appendApprovalLogEntry } from "./data/reviewActions.js";
import {
  defaultScenarioWeights,
  modelArtifactReadinessScore,
  paretoFrontier,
  rankScenarios,
  round,
  scenarioBusinessCases,
  scenarioUncertaintyAnalysis,
} from "./science/kernel.mjs";
import { BUILD_INFO } from "./components/ui.jsx";
import { SideNav, StatStrip } from "./components/AppShell.jsx";
import { ApprovalBoard } from "./pages/ApprovalBoard.jsx";
import { AsteroidScanner } from "./pages/AsteroidScanner.jsx";
import { BodyLedger } from "./pages/BodyLedger.jsx";
import { ClaimsRegister } from "./pages/ClaimsRegister.jsx";
import { CommandOverview } from "./pages/CommandOverview.jsx";
import { FormulaLab } from "./pages/FormulaLab.jsx";
import { PlatformRoadmap } from "./pages/PlatformRoadmap.jsx";
import { ProcessLab } from "./pages/ProcessLab.jsx";
import { ResearchPackages } from "./pages/ResearchPackages.jsx";
import { ScenarioCompare } from "./pages/ScenarioCompare.jsx";
import { SolarClock } from "./pages/SolarClock.jsx";
import { SourceLibrary } from "./pages/SourceLibrary.jsx";
import "./styles.css";

function App() {
  const [activePage, setActivePage] = useState("overview");
  const [navOpen, setNavOpen] = useState(false);
  const [weights, setWeights] = useState(defaultScenarioWeights);
  const [selectedScenarioId, setSelectedScenarioId] = useState("hybrid");
  const [scenarioSet, setScenarioSet] = useState("Primary set (6)");
  const [horizonYears, setHorizonYears] = useState(18);
  const [capitalUsdBn, setCapitalUsdBn] = useState(280);
  const [launchMassT, setLaunchMassT] = useState(160);
  const [selectedBodyId, setSelectedBodyId] = useState("mercury");
  const [selectedProcessId, setSelectedProcessId] = useState("vacuum-sheet");
  const [selectedFormulaId, setSelectedFormulaId] = useState("solar-flux");
  const [selectedClaimId, setSelectedClaimId] = useState("claim-mercury-flux");
  const [claimFilters, setClaimFilters] = useState({
    status: "All",
    domain: "All",
    confidence: "All",
    sourceType: "All",
    recency: "All",
    search: "",
  });
  const [approvalLog, setApprovalLog] = useState([]);

  const rankedScenarios = useMemo(() => rankScenarios(scenarios, weights), [weights]);
  const selectedScenario = rankedScenarios.find((entry) => entry.id === selectedScenarioId) || rankedScenarios[0];
  const selectedBody = bodies.find((body) => body.id === selectedBodyId) || bodies[0];
  const selectedProcess = processes.find((process) => process.id === selectedProcessId) || processes[0];
  const frontier = useMemo(() => paretoFrontier(rankedScenarios), [rankedScenarios]);
  const businessCases = useMemo(
    () =>
      scenarioBusinessCases(scenarios, {
        weights,
        capitalUsdBn,
        launchMassT,
        horizonYears,
      }),
    [weights, capitalUsdBn, launchMassT, horizonYears]
  );
  const uncertaintyAnalysis = useMemo(
    () =>
      scenarioUncertaintyAnalysis(scenarios, {
        weights,
        iterations: 256,
        seed: `${scenarioSet}-${horizonYears}-${capitalUsdBn}-${launchMassT}`,
      }),
    [weights, scenarioSet, horizonYears, capitalUsdBn, launchMassT]
  );

  const selectedFormula = formulas.find((formula) => formula.id === selectedFormulaId) || formulas[0];
  const selectedClaim = claims.find((claim) => claim.id === selectedClaimId) || claims[0];
  const shellStats = useMemo(() => {
    const evidenceItems =
      sources.length +
      claims.length +
      formulas.length +
      scenarios.length +
      sourceRegistryV2.length +
      modelArtifacts.length +
      assumptionRegistry.length;
    const verifiedClaims = claims.filter((claim) => claim.status === "Verified").length;
    const confidence = Math.round((verifiedClaims / claims.length) * 100);
    const artifactAverage = Math.round(
      modelArtifacts.reduce(
        (sum, artifact) => sum + modelArtifactReadinessScore(artifact.readinessInputs),
        0
      ) / modelArtifacts.length
    );

    return {
      evidenceItems,
      confidence,
      artifactAverage,
      openReviewItems: claims.length - verifiedClaims,
      verifiedPercent: confidence,
    };
  }, []);

  useEffect(() => {
    if (!rankedScenarios.some((scenario) => scenario.id === selectedScenarioId)) {
      setSelectedScenarioId(rankedScenarios[0]?.id ?? "hybrid");
    }
  }, [rankedScenarios, selectedScenarioId]);

  function recordApproval(target, action) {
    setApprovalLog((current) => appendApprovalLogEntry(current, target, action));
  }

  const exportActions = useMemo(() => {
    const filenames = appExportFilenames(BUILD_INFO.id);
    const baseInputs = {
      build: BUILD_INFO,
      selectedScenarioId,
      scenarioSet,
      horizonYears,
      capitalUsdBn,
      launchMassT,
      weights,
      rankedScenarios,
      businessCases,
      uncertaintyAnalysis,
      sourceRegistryV2,
      claims,
      modelArtifacts,
      assumptionRegistry,
    };

    return [
      {
        label: "Export Snapshot",
        filename: filenames.scenarioSnapshot,
        createPayload: () =>
          buildScenarioSnapshotExportPayload({
            ...baseInputs,
            exportedAt: new Date().toISOString(),
            paretoFrontier: frontier,
            selectedScenario,
            sources,
            formulas,
            scenarios,
          }),
      },
      {
        label: "Export Report",
        filename: filenames.feasibilityReport,
        createPayload: () =>
          buildFeasibilityReportExportPayload({
            ...baseInputs,
            generatedAt: new Date().toISOString(),
            frontier,
            readinessCategories,
          }),
      },
      {
        label: "Export Packages",
        filename: filenames.researchPackages,
        createPayload: () =>
          buildResearchPackagesExportPayload({
            build: BUILD_INFO,
            exportedAt: new Date().toISOString(),
          }),
      },
    ];
  }, [
    selectedScenarioId,
    scenarioSet,
    horizonYears,
    capitalUsdBn,
    launchMassT,
    weights,
    rankedScenarios,
    businessCases,
    uncertaintyAnalysis,
    sourceRegistryV2,
    claims,
    modelArtifacts,
    assumptionRegistry,
    frontier,
    selectedScenario,
  ]);

  return (
    <div className="app-shell">
      <SideNav
        activePage={activePage}
        setActivePage={setActivePage}
        navOpen={navOpen}
        setNavOpen={setNavOpen}
        verifiedPercent={shellStats.verifiedPercent}
      />
      <main className="workspace">
        <button
          className="mobile-nav-toggle"
          type="button"
          aria-expanded={navOpen}
          aria-controls="primary-navigation"
          onClick={() => setNavOpen((current) => !current)}
        >
          Menu
        </button>
        <StatStrip
          evidenceItems={shellStats.evidenceItems}
          confidence={shellStats.confidence}
          artifactAverage={shellStats.artifactAverage}
          openReviewItems={shellStats.openReviewItems}
          exportActions={exportActions}
        />
        <div className="page-content">
          {activePage === "overview" && (
            <CommandOverview
              rankedScenarios={rankedScenarios}
              selectedScenario={selectedScenario}
              setSelectedScenarioId={setSelectedScenarioId}
              recordApproval={recordApproval}
              claims={claims}
              readinessCategories={readinessCategories}
            />
          )}
          {activePage === "scenarios" && (
            <ScenarioCompare
              rankedScenarios={rankedScenarios}
              selectedScenario={selectedScenario}
              selectedScenarioId={selectedScenarioId}
              setSelectedScenarioId={setSelectedScenarioId}
              weights={weights}
              setWeights={setWeights}
              scenarioSet={scenarioSet}
              setScenarioSet={setScenarioSet}
              horizonYears={horizonYears}
              setHorizonYears={setHorizonYears}
              capitalUsdBn={capitalUsdBn}
              setCapitalUsdBn={setCapitalUsdBn}
              launchMassT={launchMassT}
              setLaunchMassT={setLaunchMassT}
              recordApproval={recordApproval}
              uncertaintyAnalysis={uncertaintyAnalysis}
              businessCases={businessCases}
            />
          )}
          {activePage === "solar" && <SolarClock />}
          {activePage === "bodies" && (
            <BodyLedger
              selectedBody={selectedBody}
              selectedBodyId={selectedBodyId}
              setSelectedBodyId={setSelectedBodyId}
            />
          )}
          {activePage === "process" && (
            <ProcessLab
              selectedProcess={selectedProcess}
              selectedProcessId={selectedProcessId}
              setSelectedProcessId={setSelectedProcessId}
            />
          )}
          {activePage === "claims" && (
            <ClaimsRegister
              claims={claims}
              selectedClaim={selectedClaim}
              selectedClaimId={selectedClaimId}
              setSelectedClaimId={setSelectedClaimId}
              filters={claimFilters}
              setFilters={setClaimFilters}
              recordApproval={recordApproval}
            />
          )}
          {activePage === "formula" && (
            <FormulaLab selectedFormula={selectedFormula} selectedFormulaId={selectedFormulaId} setSelectedFormulaId={setSelectedFormulaId} />
          )}
          {activePage === "asteroids" && <AsteroidScanner />}
          {activePage === "sources" && <SourceLibrary />}
          {activePage === "packages" && <ResearchPackages />}
          {activePage === "roadmap" && <PlatformRoadmap />}
          {activePage === "approval" && <ApprovalBoard approvalLog={approvalLog} />}
        </div>
      </main>
    </div>
  );
}

createRoot(document.getElementById("root")).render(<App />);
